import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import useAvatarStore from "../stores/avatarStore";
import alexImg from "../../../assets/images/alex_coach.png";
import oliviaImg from "../../../assets/images/olivia_coach.png";

const CoachAvatar = () => {
  const selectedAvatar = useAvatarStore((state) => state.selectedAvatar);
  const avatarEmotion = useAvatarStore((state) => state.avatarEmotion);

  const mountRef = useRef(null);

  // Mouse tracking references
  const mouseX = useRef(0);
  const mouseY = useRef(0);

  // Resolve the static image for background/profile card
  const avatarImg = selectedAvatar === "olivia" ? oliviaImg : alexImg;

  // Dynamic emotional background overlay class
  const getEmotionOverlayClass = () => {
    switch (avatarEmotion) {
      case "greeting":
        return "from-amber-500/20 to-transparent";
      case "success":
        return "from-emerald-500/20 to-transparent";
      case "confusion":
        return "from-sky-500/20 to-transparent";
      case "objection":
        return "from-amber-600/20 to-transparent";
      default:
        return "from-secondary/10 to-transparent";
    }
  };

  useEffect(() => {
    const currentContainer = mountRef.current;
    if (!currentContainer) return;

    // 1. Scene Setup
    const scene = new THREE.Scene();

    // 2. Camera Setup
    const width = currentContainer.clientWidth || 400;
    const height = currentContainer.clientHeight || 500;
    const camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 100);
    camera.position.set(0, 0, 1.8);

    // 3. Renderer Setup
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    currentContainer.appendChild(renderer.domElement);

    // 4. Custom Liquid Glass Shader Material (Gemini / OpenAI-style orb)
    const shaderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0.0 },
        uSpeechAmplitude: { value: 0.0 },
        uColor1: { value: new THREE.Color("#6366f1") },
        uColor2: { value: new THREE.Color("#a5b4fc") },
      },
      vertexShader: `
        uniform float uTime;
        uniform float uSpeechAmplitude;
        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPosition = position;

          // Organic fluid-like morphing wave formula
          float wave = sin(position.x * 6.0 + uTime * 2.2) * 
                       cos(position.y * 5.0 + uTime * 1.8) * 
                       sin(position.z * 6.0 + uTime * 2.6);
                       
          // Speech ripples
          float ripple = sin(position.y * 14.0 - uTime * 11.0) * uSpeechAmplitude;
          
          vec3 displacedPosition = position + normal * (wave * 0.045 + ripple * 0.1);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(displacedPosition, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uColor1;
        uniform vec3 uColor2;
        uniform float uTime;
        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          vec3 normal = normalize(vNormal);
          vec3 viewDir = vec3(0.0, 0.0, 1.0);

          // Fresnel glow edge reflection
          float fresnel = pow(1.0 - max(dot(normal, viewDir), 0.0), 2.0);

          // Liquid color swirls blending based on coordinates and time
          float w1 = sin(vPosition.x * 5.0 + uTime * 1.2) * 0.5 + 0.5;
          float w2 = cos(vPosition.y * 5.0 - uTime * 1.6) * 0.5 + 0.5;
          float w3 = sin(vPosition.z * 5.0 + uTime * 0.8) * 0.5 + 0.5;

          vec3 baseColor = mix(uColor1, uColor2, w1);
          vec3 highlightColor = mix(uColor2, vec3(1.0, 0.9, 0.95), w2);
          vec3 finalColor = mix(baseColor, highlightColor, w3);

          // High-end translucent alpha mapping: clear center, glowing edges
          float alpha = mix(0.28, 0.92, fresnel);

          // Add extra edge intensity glow
          finalColor += vec3(fresnel * 0.5);

          gl_FragColor = vec4(finalColor, alpha);
        }
      `,
      transparent: true,
      depthWrite: true,
      blending: THREE.NormalBlending,
    });

    // 5. Morphing Glass Orb Mesh
    const orbGeometry = new THREE.IcosahedronGeometry(0.28, 64);
    const orbMesh = new THREE.Mesh(orbGeometry, shaderMaterial);
    orbMesh.position.set(0, 0.02, 0.0);
    scene.add(orbMesh);

    // 6. Orbiting Glowing Particles
    const particleCount = 50;
    const particleGeom = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const angles = new Float32Array(particleCount);
    const radii = new Float32Array(particleCount);
    const speeds = new Float32Array(particleCount);

    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2 + Math.random() * 0.15;
      const radius = 0.45 + Math.random() * 0.1;
      angles[i] = angle;
      radii[i] = radius;
      speeds[i] = 0.5 + Math.random() * 0.7;

      positions[i * 3] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 0.06;
      positions[i * 3 + 2] = Math.sin(angle) * radius;
    }

    particleGeom.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    // Circular gradient canvas texture for glowing particles
    const pCanvas = document.createElement("canvas");
    pCanvas.width = 32;
    pCanvas.height = 32;
    const pCtx = pCanvas.getContext("2d");
    const grad = pCtx.createRadialGradient(16, 16, 0, 16, 16, 16);
    grad.addColorStop(0, "rgba(255,255,255,1.0)");
    grad.addColorStop(0.25, "rgba(255,255,255,0.75)");
    grad.addColorStop(1, "rgba(255,255,255,0)");
    pCtx.fillStyle = grad;
    pCtx.fillRect(0, 0, 32, 32);
    const particleTexture = new THREE.CanvasTexture(pCanvas);

    const particleMat = new THREE.PointsMaterial({
      size: 0.055,
      map: particleTexture,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      color: 0xffffff,
    });

    const particlePoints = new THREE.Points(particleGeom, particleMat);
    scene.add(particlePoints);

    // 7. Event Listeners
    const handleMouseMove = (e) => {
      const rect = currentContainer.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      mouseX.current = (x / rect.width) * 2 - 1;
      mouseY.current = -(y / rect.height) * 2 + 1;
    };

    window.addEventListener("mousemove", handleMouseMove);

    const handleResize = () => {
      const w = currentContainer.clientWidth;
      const h = currentContainer.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(handleResize);
    });
    resizeObserver.observe(currentContainer);

    // 8. Animation Clock & Loop
    const clock = new THREE.Clock();
    let animationFrameId = null;

    const tick = () => {
      const delta = clock.getDelta();
      const time = clock.getElapsedTime();

      // A. Uniform uTime update
      shaderMaterial.uniforms.uTime.value = time;

      // B. Dynamic speaking & listening amplitude sync
      const coachState = useAvatarStore.getState().coachState;
      let targetAmplitude = 0.0;
      if (coachState === "speaking") {
        targetAmplitude = 0.32 + Math.abs(Math.sin(time * 16.0)) * 0.68;
      } else if (coachState === "listening") {
        targetAmplitude = 0.05;
      }

      shaderMaterial.uniforms.uSpeechAmplitude.value +=
        (targetAmplitude - shaderMaterial.uniforms.uSpeechAmplitude.value) * 0.15;

      // C. Dynamic Color Lerping based on active state or emotion and coach selection
      const isOlivia = selectedAvatar === "olivia";
      let c1, c2;
      if (coachState === "speaking") {
        // Active violet and rose gradient
        c1 = isOlivia ? new THREE.Color("#0f766e") : new THREE.Color("#4f46e5");
        c2 = isOlivia ? new THREE.Color("#2dd4bf") : new THREE.Color("#f43f5e");
      } else if (coachState === "thinking") {
        // Calculating gold and amber
        c1 = new THREE.Color("#d97706");
        c2 = new THREE.Color("#fbbf24");
      } else {
        // Listening / Idle (Character specific color scheme)
        switch (avatarEmotion) {
          case "greeting":
            c1 = new THREE.Color("#f59e0b");
            c2 = new THREE.Color("#fbbf24");
            break;
          case "success":
            c1 = new THREE.Color("#10b981");
            c2 = new THREE.Color("#34d399");
            break;
          case "confusion":
            c1 = new THREE.Color("#0ea5e9");
            c2 = new THREE.Color("#38bdf8");
            break;
          case "objection":
            c1 = new THREE.Color("#f97316");
            c2 = new THREE.Color("#fb923c");
            break;
          default:
            // Alex: Calm Purple/Indigo | Olivia: Calm Teal/Emerald
            c1 = isOlivia ? new THREE.Color("#0d9488") : new THREE.Color("#6366f1");
            c2 = isOlivia ? new THREE.Color("#5eead4") : new THREE.Color("#c7d2fe");
        }
      }

      shaderMaterial.uniforms.uColor1.value.lerp(c1, 0.05);
      shaderMaterial.uniforms.uColor2.value.lerp(c2, 0.05);
      particleMat.color.lerp(c2, 0.05);

      // D. Orb breathing & mouse cursor offset lerping
      const breathY = Math.sin(time * 1.8) * 0.012;
      const targetPosX = mouseX.current * 0.15;
      const targetPosY = mouseY.current * 0.12 + breathY;

      orbMesh.position.x += (targetPosX - orbMesh.position.x) * 0.08;
      orbMesh.position.y += (targetPosY - orbMesh.position.y) * 0.08;

      // E. Update particle orbits
      const pPositions = particlePoints.geometry.attributes.position.array;
      for (let i = 0; i < particleCount; i++) {
        angles[i] += delta * speeds[i] * 0.45;
        const waveY = Math.sin(angles[i] * 3.0 + time * 1.8) * 0.05;
        
        pPositions[i * 3] = Math.cos(angles[i]) * radii[i];
        pPositions[i * 3 + 1] = waveY;
        pPositions[i * 3 + 2] = Math.sin(angles[i]) * radii[i];
      }
      particlePoints.geometry.attributes.position.needsUpdate = true;
      particlePoints.rotation.y = time * 0.05;
      particlePoints.rotation.x = 0.2;
      particlePoints.position.copy(orbMesh.position);

      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(tick);
    };

    tick();

    // 9. Clean up
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("mousemove", handleMouseMove);
      resizeObserver.disconnect();
      if (currentContainer && renderer.domElement) {
        currentContainer.removeChild(renderer.domElement);
      }

      scene.traverse((obj) => {
        if (obj.isMesh || obj.isPoints) {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach((mat) => mat.dispose());
            } else {
              obj.material.dispose();
            }
          }
        }
      });
      renderer.dispose();
    };
  }, [selectedAvatar, avatarEmotion]);

  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden bg-gray-900">
      {/* Cinematic depth blurred office room background */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-1000"
        style={{
          backgroundImage: `url(${avatarImg})`,
          filter: "blur(24px) brightness(0.65) contrast(0.9)",
          transform: "scale(1.15)", // Scale slightly to hide blurred boundary bleed
        }}
      />

      {/* Glassmorphic overlay grid for additional tech texture */}
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:30px_30px] mix-blend-overlay z-0 pointer-events-none" />

      {/* Dynamic ambient color glow overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-t ${getEmotionOverlayClass()} opacity-40 z-0 transition-all duration-1000`}
      />



      {/* WebGL Canvas Container */}
      <div ref={mountRef} className="w-full h-full relative z-10" />

      {/* Ambient particles leak */}
      <div className="absolute inset-0 pointer-events-none z-15 bg-gradient-to-r from-transparent via-white/5 to-transparent mix-blend-overlay opacity-20 animate-pulse" />
    </div>
  );
};

export default CoachAvatar;
